import { defineComponent, h, VNode } from 'vue';
// @ts-ignore
import { BTabItem, BTabs } from 'buefy/dist/esm/tabs';
// @ts-ignore
import { Icon } from '@/ui/components/icon/Icon';
import { Tooltip } from '@/ui/components/Tooltip';
import { VNodeData } from 'vue/types/vnode.d';
import { featureExplanationIcon } from '@/ui/components/icon/icons';
import { Tag } from '@/ui/components/Tag';
import { t } from '@/ui/i18n/i18n';
import {
  AdministrationTabNames,
  EntityItemsTabNames,
  FormsTabNames,
  SensorsTabNames,
  AnalyticsTabNames,
  SubscriptionTabNames,
  UserProfileTabNames,
  UserEditTabNames,
  TrainingsTabNames,
  queryTabName,
} from '@/ui/components/navigation/constants';
import { router, useCurrentRoute } from '@/ui/routes';
import { openNewTab, isNewTabKey } from '@/ui/helpers';
// @ts-ignore
import { BDropdown, BDropdownItem } from 'buefy/dist/esm/dropdown';
import { flow } from 'fp-ts/function';
import { starredItemCurried } from '@/ui/components/upgrade/StarredItem';
import { isOnFreePlan } from '@/ui/auth/auth';

type TabTypes =
  AdministrationTabNames
  | EntityItemsTabNames
  | FormsTabNames
  | SensorsTabNames
  | AnalyticsTabNames
  | SubscriptionTabNames
  | UserProfileTabNames
  | UserEditTabNames
  | TrainingsTabNames;

type UnderLineColor = 'blue' | 'orange' | 'green' | 'red' | 'purple';

export interface TabItem {
  value?: TabTypes;
  label: string;
  icon: string|VNode;
  iconPack?: string;
  tooltipLabel?: string;
  underLineColor?: UnderLineColor;
  hide?: boolean;
  starred?: boolean;
  options?: ItemOption[];
  isNew?: boolean;
}

interface ItemOption {
  value: string;
  label: string;
  icon: string|VNode;
  underLineColor: UnderLineColor;
}

interface SecondaryNavbarProps {
  value: TabTypes;
  tabItems?: TabItem[];
}

export const SecondaryNavbar = defineComponent<SecondaryNavbarProps>({
  name: 'SecondaryNavbar',
  props: ['value', 'tabItems'],

  setup(props, ctx) {
    const currentRoute = useCurrentRoute();

    const getRouteObject = (item: TabItem) => ({
      ...currentRoute,
      query: {
        [queryTabName]: item.value?.toString(),
      },
    });

    const renderTooltip = (tooltipLabel: string | undefined) => {
      if (tooltipLabel) {
        return h(Tooltip, {
          props: {
            label: tooltipLabel,
            position: 'is-right',
            multilined: true,
          },
        }, [
          featureExplanationIcon(),
        ]);
      }
      return null;
    };

    const showDropDown = (item: TabItem) => (!item.value && item.options && item.options.length > 0);

    const getUnderLineColor = (item: TabItem) => item.underLineColor || item.options?.find(option => option.value === props.value)?.underLineColor;

    const handleClick = (item: TabItem, e :MouseEvent) => {
      // Workaround for hiding dropdowns due to click intercept by BTabItem
      if (document) {
        document.body.click();
      }

      const routeObject = getRouteObject(item);
      if (routeObject.query[queryTabName]) {
        if (isNewTabKey(e)) {
          const { href } = router.resolve(getRouteObject(item));
          openNewTab(href);
        } else {
          router.push(getRouteObject(item));
        }

        return;
      }

      e.preventDefault();
      e.stopPropagation();
    };

    const starred = flow(
      (node: VNode) => [node],
      starredItemCurried(isOnFreePlan.value),
    );

    return function render() {
      return h(BTabs, {
        class: 'secondary-navbar',
        props: {
          value: props.value,
          destroyOnHide: true,
          animated: false,
        },
      },
      props.tabItems?.map((item: TabItem) =>
        item.hide ? null
          : h(BTabItem, {
            props: {
              value: item.value,
              headerClass: { 'is-active': item.options?.find(option => option.value === props.value) },
            },
          },
          [
            h('span', {
              class: [
                { 'page-title-tooltiped': item.tooltipLabel },
                'secondary-navbar__item',
                `secondary-navbar__item--${getUnderLineColor(item)}`,
              ],
              slot: 'header',
              on: {
                click: (e: MouseEvent) => {
                  handleClick(item, e);
                },
              },
            }, [
              h(Icon, {
                props: {
                  classes: 'mr-1',
                  icon: item.icon,
                  iconPack: item?.iconPack,
                },
              }),
              showDropDown(item)
                ? h(
                  BDropdown,
                  {
                    scopedSlots: {
                      trigger: ({ active }: { active: boolean }) => h('div', [
                        h(
                          'div', {
                          }, [
                            h('span', item.label),
                            renderTooltip(item.tooltipLabel),
                            h(Icon, {
                              props: {
                                classes: 'ml-1',
                                icon: active ? 'angle-up' : 'angle-down',
                              },
                            }),
                          ],
                        ),
                      ]),
                    },
                  },
                  [
                    item.options?.map(dropDownItem => h(BDropdownItem, {
                      class: [
                        'secondary-navbar__dropdown-item',
                        { 'is-active': dropDownItem.value === props.value },
                      ],
                      on: {
                        click: () => {
                          ctx.emit('input', dropDownItem.value);
                        },
                      },
                    }, [
                      h(Icon, {
                        props: {
                          classes: 'mr-1',
                          icon: dropDownItem.icon,
                        },
                      }),
                      dropDownItem.label,
                    ])),
                  ],
                )
                : item.starred ? starred(h('div', item.label)) : h('div', item.label),
              !showDropDown(item) && renderTooltip(item.tooltipLabel),
              item.isNew
                ? h(Tag, { props: { type: 'success' } }, t('new-features.new'))
                : null,
            ]),
          ])));
    };
  },
});

export function secondaryNavbar(data: VNodeData & { props: SecondaryNavbarProps }) {
  return h(SecondaryNavbar, data);
}
